import React, { useEffect, useRef } from 'react';

import { MenuContextType, useMenuContext } from '../layout/MenuContext';

function getAllSelectableMenuItems(container: HTMLElement) {
  return Array.from(
    container.querySelectorAll<HTMLElement>(
      '[data-testid^="menu-item--"], [role="menuitem"]',
    ),
  ).filter((item) => {
    const button = item as HTMLButtonElement;
    const isDisabled =
      item.getAttribute('aria-disabled') === 'true' || button.disabled;
    return !isDisabled && item.tabIndex !== -1;
  });
}

function switchToKeyboardMode(
  menuContext: MenuContextType,
  getCachedItems: () => HTMLElement[],
) {
  menuContext.setIsKeyboardNavigation(true);
  const allMenuItemsForAttr = getCachedItems();
  allMenuItemsForAttr.forEach((item) => {
    item.dataset.keyboardNavigation = 'true';
  });
}

function switchToMouseMode(
  menuContext: MenuContextType,
  getCachedItems: () => HTMLElement[],
) {
  menuContext.setIsKeyboardNavigation(false);
  const allMenuItems = getCachedItems();
  allMenuItems.forEach((item) => {
    delete item.dataset.keyboardNavigation;
  });
}

export const useMenuNavigation = (
  containerRef: React.RefObject<HTMLDivElement | null>,
) => {
  const menuContext = useMenuContext();
  const cachedItemsRef = useRef<HTMLElement[] | null>(null);

  useEffect(() => {
    const container = containerRef?.current;
    if (!container) return () => {};

    const getCachedItems = () => {
      cachedItemsRef.current ??= getAllSelectableMenuItems(container);
      return cachedItemsRef.current;
    };

    const handleKeyDown = () => {
      if (!menuContext) return;
      switchToKeyboardMode(menuContext, getCachedItems);
    };

    const handleMouseOver = (e: MouseEvent) => {
      if (!menuContext) return;
      cachedItemsRef.current = null;
      switchToMouseMode(menuContext, getCachedItems);

      const target = e.target as HTMLElement;
      const menuItem = target.closest<HTMLElement>(
        '[data-testid^="menu-item--"], [role="menuitem"]',
      );

      if (!menuItem) return;

      const testId = menuItem?.dataset.testid;
      if (testId?.startsWith('menu-item--')) {
        const keyString = testId.replace('menu-item--', '');
        const key = /^\d+$/.test(keyString) ? Number(keyString) : keyString;
        menuContext.setFocusedItemKey(key);
      }

      const button = menuItem as HTMLButtonElement;
      const isDisabled =
        menuItem?.getAttribute('aria-disabled') === 'true' || button.disabled;
      if (isDisabled || menuItem?.tabIndex === -1) return;
      menuItem?.focus();
    };

    container.addEventListener('keydown', handleKeyDown);
    container.addEventListener('mouseover', handleMouseOver);
    return () => {
      container.removeEventListener('keydown', handleKeyDown);
      container.removeEventListener('mouseover', handleMouseOver);
    };
  }, [containerRef, menuContext]);
};
